import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  addTeamMember,
  createTeam,
  getApiErrorMessage,
  getDepartment,
  getTeam,
  listTeamMemberOptions,
  removeTeamMember,
  updateAgentStatus,
  updateTeam,
} from '../../api/client';
import type { AgentStatus } from '@21k/shared';
import type {
  TicketChannelCode,
  TeamDetail,
  TeamMemberOption,
  TeamMemberOptionTeam,
  TeamMemberRow,
} from '../../api/types';
import Badge from '../../components/Badge';
import AgentStatusBadge from '../../components/AgentStatusBadge';
import AgentStatusSelect from '../../components/AgentStatusSelect';
import { useAuth } from '../../contexts/AuthContext';
import { canEditDepartments, canEditStaff, canWriteDepartments } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';
import { useToast } from '../../contexts/ToastContext';

const CHANNEL_OPTIONS: TicketChannelCode[] = [
  'PARENTHUB',
  'WEBSITE',
  'EMAIL',
  'WHATSAPP',
  'ERP',
  'MANUAL',
];

function staffLabel(user: TeamMemberOption): string {
  return `${user.firstName} ${user.lastName}`.trim() || user.email;
}

const PICKER_PAGE_SIZE = 20;

function otherTeamLabels(
  teams: TeamMemberOptionTeam[],
  currentTeamId: string,
  departmentId: string,
): string[] {
  return teams
    .filter((team) => team.teamId !== currentTeamId)
    .map((team) =>
      team.departmentId === departmentId
        ? team.teamName
        : `${team.teamName} (${team.departmentName})`,
    );
}

function teamsFromMember(member: TeamMemberRow): TeamMemberOptionTeam[] {
  return (member.user.teamMemberships ?? []).map((membership) => ({
    teamId: membership.team.id,
    teamName: membership.team.name,
    departmentId: membership.team.departmentId,
    departmentName: membership.team.department.name,
  }));
}

export default function TeamFormPage() {
  const { deptId, id } = useParams<{ deptId: string; id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();

  const [deptName, setDeptName] = useState('');
  const [team, setTeam] = useState<TeamDetail | null>(null);
  const [name, setName] = useState('');
  const [displayLevel, setDisplayLevel] = useState('First Line');
  const [level, setLevel] = useState(1);
  const [isFirstLine, setIsFirstLine] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [channelMappings, setChannelMappings] = useState<TicketChannelCode[]>([]);
  const [userOptions, setUserOptions] = useState<TeamMemberOption[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<TeamMemberOption | null>(null);
  const [pickerSearchInput, setPickerSearchInput] = useState('');
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerHasMore, setPickerHasMore] = useState(false);
  const [pickerTotal, setPickerTotal] = useState(0);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerLoadingMore, setPickerLoadingMore] = useState(false);
  const pickerListRef = useRef<HTMLDivElement | null>(null);
  const pickerBusyRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const [mutatingMember, setMutatingMember] = useState(false);

  const canEdit =
    currentUser && (isNew ? canWriteDepartments(currentUser) : canEditDepartments(currentUser));
  const canManageAgentStatus = currentUser ? canEditStaff(currentUser) : false;

  useEffect(() => {
    if (!deptId) return;
    getDepartment(deptId)
      .then((dept) => setDeptName(dept.name))
      .catch((err) => setError(getApiErrorMessage(err)));
  }, [deptId]);

  const loadPickerPage = useCallback(
    async (nextPage: number, nextSearch: string, append: boolean) => {
      if (!id || id === 'new') return;
      if (append && pickerBusyRef.current) return;
      pickerBusyRef.current = true;
      if (append) setPickerLoadingMore(true);
      else setPickerLoading(true);
      try {
        const result = await listTeamMemberOptions({
          q: nextSearch.trim() || undefined,
          page: nextPage,
          pageSize: PICKER_PAGE_SIZE,
          excludeTeamId: id,
        });
        setUserOptions((prev) => {
          if (!append) return result.items;
          const seen = new Set(prev.map((user) => user.id));
          return [...prev, ...result.items.filter((user) => !seen.has(user.id))];
        });
        setPickerPage(result.page);
        setPickerHasMore(result.hasMore);
        setPickerTotal(result.total);
      } catch (err) {
        setError(getApiErrorMessage(err));
      } finally {
        pickerBusyRef.current = false;
        setPickerLoading(false);
        setPickerLoadingMore(false);
      }
    },
    [id],
  );

  useEffect(() => {
    if (isNew || !id) {
      setLoading(false);
      return;
    }
    getTeam(id)
      .then((t) => {
        setTeam(t);
        setName(t.name);
        setDisplayLevel(t.displayLevel);
        setLevel(t.level);
        setIsFirstLine(t.isFirstLine);
        setIsActive(t.isActive);
        setChannelMappings(t.channelMappings.map((m) => m.channel));
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  function toggleChannel(ch: TicketChannelCode) {
    setChannelMappings((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch],
    );
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit || !deptId) return;
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        const created = await createTeam({
          departmentId: deptId,
          name: name.trim(),
          displayLevel: displayLevel.trim(),
          level,
          isFirstLine,
          channelMappings: channelMappings.length ? channelMappings : undefined,
        });
        navigate(PLATFORM_CONFIG.departmentTeam(deptId, created.id));
      } else if (id) {
        const updated = await updateTeam(id, {
          name: name.trim(),
          displayLevel: displayLevel.trim(),
          level,
          isFirstLine,
          isActive,
        });
        setTeam(updated);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (isNew || !id) return;
    const timer = window.setTimeout(() => {
      setPickerSearch(pickerSearchInput.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [pickerSearchInput, isNew, id]);

  useEffect(() => {
    if (isNew || !id) return;
    void loadPickerPage(1, pickerSearch, false);
  }, [id, isNew, pickerSearch, loadPickerPage]);

  async function handleAddMember(moveFromOtherTeams = false) {
    if (!id || id === 'new' || !selectedStaff) return;
    setError('');
    setMutatingMember(true);
    try {
      const updated = await addTeamMember(id, selectedStaff.id, { moveFromOtherTeams });
      setTeam(updated);
      setSelectedStaff(null);
      await loadPickerPage(1, pickerSearch, false);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setMutatingMember(false);
    }
  }

  async function handleRemoveMember(userId: string) {
    if (!id || id === 'new' || !deptId) return;
    const member = team?.members.find((row) => row.userId === userId);
    const memberTeams = member ? teamsFromMember(member) : [];
    const name = member
      ? `${member.user.firstName} ${member.user.lastName}`.trim()
      : 'this staff member';
    const lastTeamInDepartment = !memberTeams.some(
      (membership) => membership.departmentId === deptId && membership.teamId !== id,
    );
    const confirmed = window.confirm(
      lastTeamInDepartment
        ? `Remove ${name} from this team? They will also be removed from ${deptName || 'this department'} because this is their only team here.`
        : `Remove ${name} from this team?`,
    );
    if (!confirmed) return;
    setError('');
    setMutatingMember(true);
    try {
      await removeTeamMember(id, userId);
      const updated = await getTeam(id);
      setTeam(updated);
      await loadPickerPage(1, pickerSearch, false);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setMutatingMember(false);
    }
  }

  function handlePickerScroll() {
    const el = pickerListRef.current;
    if (!el || pickerLoadingMore || pickerLoading || !pickerHasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      void loadPickerPage(pickerPage + 1, pickerSearch, true);
    }
  }

  async function handleMemberStatusChange(userId: string, status: AgentStatus) {
    if (!team) return;
    setUpdatingMemberId(userId);
    try {
      const updated = await updateAgentStatus(userId, status);
      setTeam({
        ...team,
        members: team.members.map((member) =>
          member.userId === userId
            ? {
                ...member,
                user: {
                  ...member.user,
                  agentStatus: updated.agentStatus as AgentStatus,
                  statusUpdatedAt: updated.statusUpdatedAt,
                },
              }
            : member,
        ),
      });
    } catch (err) {
      showToast({
        title: 'Failed to update agent status',
        label: getApiErrorMessage(err),
      });
    } finally {
      setUpdatingMemberId(null);
    }
  }

  const selectedStaffHasOtherDeptTeam = Boolean(
    selectedStaff &&
      deptId &&
      id &&
      selectedStaff.teams.some(
        (membership) => membership.departmentId === deptId && membership.teamId !== id,
      ),
  );

  if (!deptId) {
    return <div className="alert alert-error">Department not specified.</div>;
  }

  if (!canEdit) {
    return (
      <div className="alert alert-error">
        You do not have permission to manage teams.{' '}
        <Link to={PLATFORM_CONFIG.departmentTeams(deptId)}>Back to teams</Link>
      </div>
    );
  }

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <>
      <div className="page-header">
        <div>
          <Link to={PLATFORM_CONFIG.departmentTeams(deptId)} className="page-breadcrumb">
            ← Teams · {deptName}
          </Link>
          <h1>{isNew ? 'New team' : name}</h1>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="team-detail-layout">
        <form className="card card-body team-detail-main" onSubmit={handleSave}>
          <div className="form-group">
            <label>Name</label>
            <input
              className="form-control"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Display level</label>
            <input
              className="form-control"
              value={displayLevel}
              onChange={(e) => setDisplayLevel(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Numeric level</label>
            <input
              type="number"
              min={1}
              max={99}
              className="form-control"
              value={level}
              onChange={(e) => setLevel(Number(e.target.value))}
              required
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={isFirstLine}
                onChange={(e) => setIsFirstLine(e.target.checked)}
              />{' '}
              First-line team (receives initial assignment)
            </label>
          </div>
          {isNew && (
            <div className="form-group">
              <label>Channel restrictions (optional)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {CHANNEL_OPTIONS.map((ch) => (
                  <label key={ch} style={{ fontSize: '0.85rem' }}>
                    <input
                      type="checkbox"
                      checked={channelMappings.includes(ch)}
                      onChange={() => toggleChannel(ch)}
                    />{' '}
                    {ch}
                  </label>
                ))}
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0.35rem 0 0' }}>
                Leave empty for catch-all (all channels).
              </p>
            </div>
          )}
          {!isNew && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />{' '}
                Active
              </label>
            </div>
          )}
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create team' : 'Save changes'}
          </button>
        </form>

        {!isNew && team && (
          <div className="card card-body team-detail-sidebar">
            <h3 className="summary-card-title">Members</h3>
            {team.members.length === 0 ? (
              <p className="team-members-empty">No staff on this team yet.</p>
            ) : (
              <div>
                {team.members.map((m) => {
                  const otherTeams = otherTeamLabels(teamsFromMember(m), team.id, deptId);
                  return (
                    <div key={m.id} className="team-member-row">
                      <div className="team-member-top">
                        <div className="team-member-identity">
                          <span className="team-member-name">
                            {m.user.firstName} {m.user.lastName}
                            {!m.user.isActive && (
                              <Badge label="Inactive" className="badge-warning" />
                            )}
                          </span>
                          {otherTeams.length > 0 && (
                            <span className="team-member-meta">
                              Also on {otherTeams.join(', ')}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn-danger-ghost"
                          disabled={mutatingMember}
                          onClick={() => void handleRemoveMember(m.userId)}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="agent-status-row">
                        <AgentStatusBadge status={m.user.agentStatus} />
                        {canManageAgentStatus && (
                          <AgentStatusSelect
                            value={m.user.agentStatus}
                            disabled={updatingMemberId === m.userId}
                            onChange={(status) => void handleMemberStatusChange(m.userId, status)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="staff-picker">
              <input
                className="form-control"
                type="search"
                placeholder="Search staff by name or email"
                value={pickerSearchInput}
                onChange={(e) => setPickerSearchInput(e.target.value)}
              />
              <div
                ref={pickerListRef}
                className="staff-picker-list"
                onScroll={handlePickerScroll}
              >
                {pickerLoading && userOptions.length === 0 ? (
                  <p className="staff-picker-status">Loading staff…</p>
                ) : userOptions.length === 0 ? (
                  <p className="staff-picker-status">
                    {pickerSearch
                      ? 'No staff match this search.'
                      : 'All active staff are already on this team.'}
                  </p>
                ) : (
                  userOptions.map((u) => {
                    const otherTeams = otherTeamLabels(u.teams, team.id, deptId);
                    const selected = selectedStaff?.id === u.id;
                    return (
                      <button
                        key={u.id}
                        type="button"
                        className={`staff-picker-item${selected ? ' is-selected' : ''}`}
                        onClick={() => setSelectedStaff(u)}
                      >
                        <span className="staff-picker-item-name">{staffLabel(u)}</span>
                        <span className="staff-picker-item-meta">
                          {u.email}
                          {otherTeams.length > 0 ? ` — already on ${otherTeams.join(', ')}` : ''}
                        </span>
                      </button>
                    );
                  })
                )}
                {pickerLoadingMore && (
                  <p className="staff-picker-status">Loading more…</p>
                )}
              </div>
              <div className="team-add-member-row">
                <button
                  type="button"
                  className="btn-accent-compact"
                  disabled={!selectedStaff || mutatingMember}
                  onClick={() => void handleAddMember(false)}
                >
                  Add
                </button>
                {selectedStaffHasOtherDeptTeam && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={!selectedStaff || mutatingMember}
                    onClick={() => void handleAddMember(true)}
                  >
                    Move here
                  </button>
                )}
                {pickerTotal > 0 && (
                  <span className="staff-picker-count">
                    {userOptions.length} of {pickerTotal}
                  </span>
                )}
              </div>
            </div>
            <p className="team-add-member-hint">
              Staff can belong to more than one team. Use Move here to remove them from their
              other team in this department and assign them here.
            </p>
          </div>
        )}
      </div>
    </>
  );
}
